import React, { useState, useEffect, useRef } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';

import { RTL_LANGUAGES } from 'constants/languages';
import { sanitizeString } from 'utils/xss';
import { useLazyQuery, useReactiveVar, useMutation } from '@apollo/client';
import CheckIcon from 'components/Icons/CheckIcon';
import LockIcon from 'components/Icons/LockIcon';
import UnlockIcon from 'components/Icons/UnlockIcon';
import utils from 'utils/utils';

import {
  Row,
  Col,
  Checkbox,
  Dropdown,
  Avatar,
  Menu,
  Space,
  Popover,
  Input,
  Button,
  Select,
  AutoComplete,
  Tooltip,
  Divider,
} from 'antd';

import MenuIcon from 'components/Icons/MenuIcon';
import FailedIcon from 'components/Icons/FailedIcon';
import SuccessIcon from 'components/Icons/SuccessIcon';
import EditIcon from 'components/Icons/EditIcon';
import EyeIcon from 'components/Icons/EyeIcon';
import FreshbookIcon from 'components/Icons/FreshbookIcon';
import QuickbookIcon from 'components/Icons/QuickbookIcon';
import XeroIcon from 'components/Icons/XeroIcon';
import Tour from 'components/Tour';
import MorningInvoiceIcon from 'components/Icons/MorningInvoiceIcon';
import ProposalItemTag from './components/ProposalItemTag';

import {
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_ITEM_ACTIONS,
  TRASH_PROPOSAL_ITEM_ACTIONS,
  PROPOSAL_STATUS,
} from 'constants/index';

import {
  cache,
  getUserId,
  userVar,
  proposalsClientVar,
  userProfileVar,
  featureFlagsVar,
} from 'graphql/cache';
import { USER_DETAILS_UPDATE } from 'graphql/mutations/userMutations';
import { GET_PROPOSALS_CLIENTS } from 'graphql/queries/proposalQueries';
import { proposalItem } from 'helpers/grid';
import { canUserLock, canUserUnlock, getLockedByUserName } from 'helpers/proposal';
import useWindowDimensions from 'hooks/useWindowDimensions';

import './ProposalItem.scss';
import { KeyIcon } from 'components/Icons';
import { getUserDateFormatForSchedule } from 'pages/Proposal/components/Publish/modals/PublishModals/PublishedProposalModal/ScheduleModel';

const { SubMenu } = Menu;

/**
 * Item
 * @param {boolean} highlighted - Highlighted
 * @param {string} status - Status
 */
const Item = styled(Row)`
  ${(props) =>
    props.highlighted && `border: 1px solid ${PROPOSAL_STATUS_COLORS[props.status]?.main};`}
  border-left: ${(props) =>
    `4px solid ${
      PROPOSAL_STATUS_COLORS[props.status] && PROPOSAL_STATUS_COLORS[props.status]?.main
    }`};
`;

/**
 * Status label
 * @param {string} status - Status
 */
const StatusLabel = styled.div`
  background: ${(props) => [props.status] && PROPOSAL_STATUS_COLORS[props.status]?.background};
  color: ${(props) =>
    PROPOSAL_STATUS_COLORS[props.status] && PROPOSAL_STATUS_COLORS[props.status]?.main};
  border: 1px solid
    ${(props) => PROPOSAL_STATUS_COLORS[props.status] && PROPOSAL_STATUS_COLORS[props.status]?.main};
`;

/**
 * Dismiss badge
 * @param {string} status - Status
 */
const DismissBadge = styled(Button)`
  background: ${(props) => [props.status] && PROPOSAL_STATUS_COLORS[props.status]?.main};
  color: #fff;
  width: 20px !important;
  min-width: 20px !important;
  height: 20px !important;
`;

/**
 * Option
 */
const { Option } = Select;

/**
 * Proposal item
 * @param {object} teamInfo - Team info
 * @param {string} pnum - Proposal number
 * @param {string} name - Name
 * @param {string} avatar - Avatar
 * @param {string} client - Client
 * @param {string} watched - Watched
 * @param {string} quote - Quote
 * @param {boolean} hasNegativePrice - Has negative price
 * @param {number} opened - Opened
 * @param {array} emailLogs - Email logs
 * @param {boolean} trash - Trash
 * @param {string} lastSeen - Last seen
 * @param {string} clientIP - Client IP
 * @param {string} status - Status
 * @param {string} prevState - Previous state
 * @param {function} onClickItem - On click item
 * @param {string} proposalId - Proposal ID
 * @param {boolean} isLocked - Is locked
 * @param {object} variables - Variables
 * @param {boolean} isDeletable - Is deletable
 * @param {array} tags - Tags
 * @param {array} tagsData - Tags data
 * @param {number} proposalIndex - Proposal index
 * @param {function} setTagWrapperWidth - On set tag wrapper width
 * @param {array} proposalList - Proposal list
 * @param {string} language - Language
 * @param {boolean} isSecured - Is secured
 * @param {boolean} isHighlighted - Is highlighted
 * @param {function} handleSignedSeen - On handle signed seen
 * @param {object} loadingMap - Loading map
 * @param {boolean} isScheduled - Is scheduled
 * @param {string} scheduleDate - Schedule date
 * @param {string} lastStatusUpdateTime - Last status update time
 * @param {object} lockedData - Locked data
 * @param {object} lockLoadingMap - Lock loading map
 * @param {boolean} isSelected - Is selected
 * @param {function} onToggleSelect - On toggle select
 * @param {object} rest - Rest
 */
const ProposalItem = ({
  teamInfo,
  pnum,
  name,
  avatar,
  client,
  watched,
  quote,
  hasNegativePrice,
  opened,
  emailLogs,
  trash,
  lastSeen,
  clientIP,
  status,
  prevState,
  onClickItem,
  proposalId,
  isLocked,
  variables,
  isDeletable,
  tags,
  tagsData,
  proposalIndex,
  setTagWrapperWidth,
  proposalList,
  language,
  isSecured,
  isHighlighted,
  handleSignedSeen,
  loadingMap,
  isScheduled,
  scheduleDate,
  lastStatusUpdateTime,
  lockedData,
  lockLoadingMap,
  isSelected,
  onToggleSelect,
  ...rest
}) => {
  const user = useReactiveVar(userVar);
  const cachedProfile = useReactiveVar(userProfileVar);
  const proposalsClients = useReactiveVar(proposalsClientVar);
  const { width } = useWindowDimensions();
  const isMobile = width < 992;

  const profile = { ...cachedProfile };
  const [visible, toggleEdit] = useState(false);
  const [visibleTags, setvisibleTags] = useState(false);
  const [clientVisible, toggleClientEdit] = useState(false);
  const [proposalTitle, setProposalTitle] = useState(name);
  const [proposalTitleError, setProposalTitleError] = useState('');
  const [clientName, setClientName] = useState(client);
  const [clientNameError, setClientNameError] = useState('');
  const [clientNames, setClientNames] = useState(proposalsClients);
  const tagRef = useRef(null);
  const tourRef = useRef(null);
  const [openTour, setOpenTour] = useState(false);
  const featureFlag = useReactiveVar(featureFlagsVar);

  const tourSteps = [
    {
      title: 'Speed up your workflow',
      description:
        'Save time by turning your proposal into a reusable template for quick customization and faster project setup.',
      placement: 'topRight',
      target: () => tourRef.current,
    },
  ];

  useEffect(() => {
    if (status === 'PUBLISHED' && !profile.seenTour && proposalList.length === 1) {
      setOpenTour(true);
    }
  }, [profile.seenTour, status, proposalList]);

  useEffect(() => {
    if (name !== proposalTitle) setProposalTitle(name);
  }, [name]); //eslint-disable-line

  useEffect(() => {
    if (tagRef.current?.offsetWidth) {
      if (width < 768) {
        // for smaller device its fixed value
        setTagWrapperWidth(width - 135);
        return;
      }

      // calculate width of tag wrapper div
      // calculating width for only for 1 item is enough as rest will be same
      setTagWrapperWidth(tagRef.current.offsetWidth - 145);
    }
  }, [tagRef, tagsData]); //eslint-disable-line

  const isRTL = RTL_LANGUAGES.includes(language);

  const [updateProfile] = useMutation(USER_DETAILS_UPDATE, {
    errorPolicy: 'none',
    async onCompleted() {
      cache.evict('fetchUser');
      userProfileVar(profile);
    },
  });

  const closeTour = (saveTemplate = false) => {
    profile['seenTour'] = true;
    updateProfile({
      variables: {
        id: getUserId(),
        profile: profile,
      },
    });
    userProfileVar(profile);
    setOpenTour(false);
    saveTemplate && onClickItem({ actionType: 'save_as_template', proposalId });
  };

  /**
   * Handle visible change
   * @param {boolean} visible - Visible
   */
  const handleVisibleChange = (visible) => {
    closeTour(visible);
  };

  /**
   * Load proposal clients
   */
  const [loadProposalClients] = useLazyQuery(GET_PROPOSALS_CLIENTS, {
    skip: !getUserId() || proposalsClients.length,
    variables: {
      uid: getUserId(),
      fetchPolicy: 'cache-and-network',
    },
    onCompleted: ({ fetchProposals }) => {
      let tempClientNames = _.compact(
        _.map(
          fetchProposals?.proposals,
          (p) => p.client && p.client.contact && { value: p.client.contact }
        )
      );

      tempClientNames = _.uniqBy(tempClientNames, 'value');

      setClientNames(tempClientNames);
      proposalsClientVar(tempClientNames);
    },
  });

  /**
   * Handle click
   * @param {object} e - Event
   */
  const handleClick = (e) => {
    e.domEvent.stopPropagation();

    const { key } = e;
    let actionType = trash
      ? TRASH_PROPOSAL_ITEM_ACTIONS[key]?.key
      : PROPOSAL_ITEM_ACTIONS[key]?.key;
    if (!actionType) {
      PROPOSAL_ITEM_ACTIONS['item_2'].value.forEach((item, itemKey) => {
        if (key === item.key) {
          actionType = item.key;
        }
      });
    }

    if (actionType === 'lock_unlock') {
      onClickItem({
        actionType: actionType,
        proposalId,
        value: !lockedData,
      });
    } else {
      onClickItem({
        actionType: actionType,
        proposalId,
      });
    }
  };

  /**
   * Handle key command
   * @param {object} e - Event
   */
  const handleKeyCommand = (e) => {
    if (e.key === 'Escape') {
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        window.removeEventListener('keydown', handleKeyCommand, true);
        toggleEdit(visible);
      }
    }
  };

  /**
   * Save by enter
   * @param {object} e - Event
   */
  const saveByEnter = (e) => {
    const x = e.which || e.keyCode;
    if (e.key === 'Enter' || x === 13) {
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        onClickItem({
          actionType: 'update_title',
          proposalId,
          value: proposalTitle,
        });
        toggleEdit(false);
      }
    }
  };

  /**
   * Can share proposal
   */
  const canShareProposal =
    teamInfo?.userRole === 'manager' ||
    (teamInfo?.userId &&
      (teamInfo?.userId === teamInfo?.uid || teamInfo?.userId === teamInfo?.auid))
      ? true
      : false;

  /**
   * Menu
   * @returns
   */
  const menu = () => {
    let proposalItemsActions = JSON.parse(JSON.stringify(PROPOSAL_ITEM_ACTIONS));
    // if user is not connected to xero, remove the xero invoice option
    if (!user?.xero?.connected) {
      proposalItemsActions.item_2.value = proposalItemsActions.item_2.value.filter(
        (item) => item.key !== 'generate_xero_invoice'
      );
    }
    if (!isDeletable) {
      delete proposalItemsActions.item_7;
    }

    if (
      isScheduled ||
      (status !== PROPOSAL_STATUS.published &&
        status !== PROPOSAL_STATUS.sent &&
        status !== PROPOSAL_STATUS.seen)
    ) {
      delete proposalItemsActions.item_10;
    }
    if (isScheduled && scheduleDate) {
      const formattedDate = moment(scheduleDate).format(
        `${getUserDateFormatForSchedule(user)}  | hh:mm A`
      );
      proposalItemsActions.item_11.subTitle = `(Scheduled: ${formattedDate})`;
    } else {
      delete proposalItemsActions.item_11;
    }

    const canLockProposal = canUserLock(user);
    const canUnlockProposal = canUserUnlock(user, lockedData);
    const isLockedByUser = lockedData?.uid === user._id;

    return (
      <Menu onClick={handleClick} className="proposal-actions">
        {lockedData && !isLockedByUser && (
          <Menu.Item key="lock_unlock" className="lock-unlock-item">
            <div className="content">
              <LockIcon className="lock-icon" />
              <span className="content-text">
                Locked by: <span className="name">{getLockedByUserName(lockedData)}</span>
              </span>
            </div>
          </Menu.Item>
        )}
        {Object.keys(proposalItemsActions).map((item) => {
          if (proposalItemsActions[item].key === 'copy_link') {
            const disabled = isLocked || status === 'DRAFT';
            return (
              <Menu.Item
                key={item}
                // disabled={disabled}
                className={`${disabled ? 'ant-dropdown-menu-item-disabled' : ''}`}>
                <Tooltip title={!isLocked && disabled && 'Publish proposal to copy link'}>
                  <div>{proposalItemsActions[item].value}</div>
                </Tooltip>
              </Menu.Item>
            );
          }

          return proposalItemsActions[item].key === 'approved_declned' ? (
            status !== 'DECLINED' && status !== 'APPROVED' && (
              <Menu.Item key={item} className="approved-declined" disabled={lockedData || isLocked}>
                <Button
                  className="medium-btn transparent-btn"
                  icon={<SuccessIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickItem({ actionType: 'approve', proposalId });
                  }}
                  disabled={lockedData}>
                  Approve
                </Button>

                <Button
                  className="medium-btn transparent-btn"
                  icon={<FailedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickItem({ actionType: 'decline', proposalId });
                  }}
                  disabled={lockedData}>
                  Decline
                </Button>
              </Menu.Item>
            )
          ) : proposalItemsActions[item].key === 'lock_unlock' ? (
            status === 'APPROVED' ? null : lockedData ? (
              !canUnlockProposal ? null : (
                <Menu.Item key={item}>
                  <UnlockIcon className="lock-icon" /> Unlock
                </Menu.Item>
              )
            ) : !canLockProposal ? null : (
              <Menu.Item key={item}>
                <LockIcon className="lock-icon" /> Lock
              </Menu.Item>
            )
          ) : proposalItemsActions[item].key === 'share_proposal' &&
            !canShareProposal ? null : (status === 'DECLINED' || status === 'APPROVED') &&
            proposalItemsActions[item].key !== 'invoice submenu' ? (
            <Menu.Item
              key={item}
              disabled={isLocked && proposalItemsActions[item].key !== 'download_pdf'}>
              {proposalItemsActions[item].value}
            </Menu.Item>
          ) : proposalItemsActions[item].key === 'invoice submenu' ? (
            <>
              {!hasNegativePrice && (
                <SubMenu
                  key={item}
                  disabled={isLocked}
                  title="Generate Invoice"
                  onTitleClick={(e) => {
                    e.domEvent.stopPropagation();
                  }}>
                  {!isLocked &&
                    proposalItemsActions[item].value.map((subitems) => (
                      <Menu.Item
                        key={subitems.key}
                        icon={
                          subitems.icon === 'FRESHBOOK' ? (
                            <FreshbookIcon />
                          ) : subitems.icon === 'QUICKBOOK' ? (
                            <QuickbookIcon />
                          ) : subitems.icon === 'MORNINGINVOICE' ? (
                            <MorningInvoiceIcon />
                          ) : subitems.icon === 'XERO' ? (
                            <XeroIcon />
                          ) : null
                        }>
                        {subitems.value}
                      </Menu.Item>
                    ))}
                </SubMenu>
              )}
            </>
          ) : (
            proposalItemsActions[item].key !== 'change_to_previous_state' && (
              <Menu.Item
                className={proposalItemsActions[item].key === 'delete' && 'delete-text'}
                key={item}
                disabled={
                  (lockedData && proposalItemsActions[item].key === 'edit') ||
                  (lockedData && proposalItemsActions[item].key === 'delete') ||
                  (lockedData && proposalItemsActions[item].key === 'duplicate') ||
                  (isLocked && proposalItemsActions[item].key !== 'download_pdf')
                }>
                {proposalItemsActions[item].value}
                {proposalItemsActions[item].subTitle && (
                  <div>
                    <span className="sub-text">{proposalItemsActions[item].subTitle}</span>
                  </div>
                )}
              </Menu.Item>
            )
          );
        })}
      </Menu>
    );
  };

  /**
   * Trash menu
   * @returns
   */
  const trashMenu = () => {
    return (
      <Menu onClick={handleClick} className="proposal-actions">
        {Object.keys(TRASH_PROPOSAL_ITEM_ACTIONS).map((item) => {
          return TRASH_PROPOSAL_ITEM_ACTIONS[item].key === 'delete' ? (
            <Menu.Item className="delete-text" key={item}>
              {TRASH_PROPOSAL_ITEM_ACTIONS[item].value}
            </Menu.Item>
          ) : (
            <Menu.Item className="restore-text" key={item}>
              {TRASH_PROPOSAL_ITEM_ACTIONS[item].value}
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  /**
   * Proposal name
   * @returns
   */
  const proposalName = utils.changeVariable(variables, proposalTitle, user?.profile?.dateFormat);
  let proposalClientName = utils.changeVariable(variables, clientName, user?.profile?.dateFormat);
  proposalClientName = proposalClientName.replace(/<\/?p>/g, '');

  /**
   * Render status tooltip
   * @param {string} statusLabel - Status label
   * @returns
   */
  const renderStatusTooltip = (statusLabel) => {
    const preText = {
      DRAFT: 'Last edit: ',
      PDF: 'Last download: ',
      OPENED: 'Last opened: ',
      DOWNLOADED: 'Last downloaded: ',
    };

    return lastStatusUpdateTime ? (
      <Popover
        overlayClassName="proposal-status-popover"
        content={
          <span>
            {lastStatusUpdateTime && (
              <>
                {preText[status]}
                {moment(lastStatusUpdateTime).format('MMM Do, YYYY, HH:mm')}
                <br />
              </>
            )}
          </span>
        }>
        {statusLabel}
      </Popover>
    ) : (
      statusLabel
    );
  };

  /**
   * Tags DOM
   * @returns
   */
  const tagsDom = (
    <div
      className="sub-header-wrapper"
      onClick={(e) => {
        e.stopPropagation();
      }}>
      <Popover
        overlayClassName="proposal-number-popover proposal-item-view-info-popover"
        content={'Proposal ID Number'}>
        <label className="subproposal">{pnum}</label>
      </Popover>
      <div className="seprator" />
      <div
        className={`proposal-tags-wrapper ${tags?.length ? 'tags-available' : ''} ${
          visibleTags ? 'active' : ''
        }`}>
        <ProposalItemTag
          tags={tags || []}
          tagsData={tagsData || []}
          visibleTags={visibleTags}
          setvisibleTags={setvisibleTags}
          handleKeyCommand={handleKeyCommand}
          proposalList={proposalList}
          {...rest}
        />
      </div>
    </div>
  );

  /**
   * Render proposal item
   * @returns
   */
  return (
    <Item
      className={`item proposal-item ${avatar || lockedData ? 'with-avatar' : ''} ${
        lockLoadingMap?.[proposalId] ? 'lock-action-loading' : ''
      }`}
      status={status}
      highlighted={isHighlighted?.toString()}
      onClick={
        lockedData
          ? () => {}
          : (e) => {
              if (e.target.className.includes('ant-checkbox-wrapper')) {
                return;
              }
              onClickItem({ actionType: 'edit', proposalId });
            }
      }>
      {lockLoadingMap?.[proposalId] && <LoadingOutlined className="loading-indicator" />}

      {onToggleSelect && (
        <Col {...proposalItem.checkbox} className="value value-checkbox">
          <Checkbox
            checked={!!isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(proposalId);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Col>
      )}

      <Col
        {...proposalItem.proposal}
        className="value value-proposal-title"
        {...(proposalIndex === 0 ? { ref: tagRef } : {})}>
        <div className="proposal-name-wrapper">
          {lockedData ? (
            <div className="avatar-wrapper">
              <Tooltip title={'Locked by: ' + getLockedByUserName(lockedData)}>
                <Avatar
                  size={40}
                  style={{
                    backgroundColor: 'rgba(5, 3, 77, 1)',
                    lineHeight: '36px',
                    color: '#fff',
                  }}
                  icon={<LockIcon />}
                />
              </Tooltip>
            </div>
          ) : avatar ? (
            <div className="avatar-wrapper">{avatar}</div>
          ) : (
            <div className="avatar-wrapper"></div>
          )}

          <div className="name-wrapper">
            <div className="proposal-item-title" title={proposalName}>
              {featureFlag?.PASSWORD_PROTECTED_PROPOSAL && isSecured && (
                <Popover content="Password protected" overlayClassName="password-protected-tooltip">
                  <KeyIcon />
                </Popover>
              )}
              <span className={`title ${isRTL ? 'rtl-text' : ''}`}>{proposalName}</span>
              <Popover
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onVisibleChange={() => {
                  if (!visible) {
                    window.addEventListener('keydown', handleKeyCommand, true);
                  } else {
                    window.removeEventListener('keydown', handleKeyCommand, true);
                  }
                  toggleEdit(!visible);
                }}
                overlayClassName="edit-container-popover"
                trigger="click"
                visible={visible}
                content={
                  <div onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={proposalTitle}
                      onChange={(e) => {
                        if (proposalTitleError) setProposalTitleError('');
                        setProposalTitle(
                          !!sanitizeString(e.target.value).trim()
                            ? sanitizeString(e.target.value)
                            : ''
                        );
                      }}
                      onKeyPress={saveByEnter}
                    />
                    <p className="error-field">{proposalTitleError}</p>
                    <div className="button-wrapper">
                      <Space>
                        <Button
                          className="medium-btn secondary-btn"
                          onClick={() => {
                            if (!proposalTitle) {
                              return setProposalTitleError('Proposal name cannot be empty.');
                            }

                            onClickItem({
                              actionType: 'update_title',
                              proposalId,
                              value: proposalTitle,
                            });
                            toggleEdit(false);
                            setProposalTitle(proposalTitle);
                          }}>
                          Save
                        </Button>
                        <Button
                          className="medium-btn grey-btn"
                          onClick={() => {
                            window.removeEventListener('keydown', handleKeyCommand, true);
                            toggleEdit(false);
                            setProposalTitle(name);
                            setProposalTitleError('');
                          }}>
                          Cancel
                        </Button>
                      </Space>
                    </div>
                  </div>
                }>
                {status !== 'APPROVED' && prevState !== 'APPROVED' && (
                  <EditIcon className={`edit-icon ${visible ? 'proposal-item-name-active' : ''}`} />
                )}
              </Popover>
            </div>

            {isMobile ? null : tagsDom}
          </div>
        </div>
      </Col>

      {isMobile && (
        <Col xs={24} className="divider-col">
          <Divider />
        </Col>
      )}

      <Col {...proposalItem.client} className="value value-prospect-name">
        <span className="value-client">{proposalClientName}</span>

        <Popover
          onClick={(e) => {
            e.stopPropagation();
            loadProposalClients();
          }}
          onVisibleChange={() => toggleClientEdit(!clientVisible)}
          trigger="click"
          overlayClassName="edit-container-popover"
          visible={clientVisible}
          content={
            <div onClick={(e) => e.stopPropagation()}>
              <AutoComplete
                filterOption={(inputValue, option) =>
                  option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
                value={proposalClientName}
                onChange={(value) => {
                  if (clientNameError) setClientNameError('');
                  const limitedValue = utils.limitInputLength(value);
                  setClientName(
                    !!sanitizeString(limitedValue).trim() ? sanitizeString(limitedValue) : ''
                  );
                }}>
                {clientNames.map((client, clientIndex) => (
                  <Option key={`${client.value}${clientIndex}`} value={client.value}>
                    {client.value}
                  </Option>
                ))}
              </AutoComplete>
              <p className="error-field">{clientNameError}</p>

              <div className="button-wrapper">
                <Space>
                  <Button
                    className="medium-btn secondary-btn"
                    onClick={() => {
                      if (!proposalClientName) {
                        return setClientNameError('Prospect name cannot be empty');
                      }
                      onClickItem({
                        actionType: 'update_client',
                        proposalId,
                        value: proposalClientName,
                      });
                      toggleClientEdit(false);
                    }}>
                    Save
                  </Button>
                  <Button
                    className="medium-btn grey-btn"
                    onClick={() => {
                      toggleClientEdit(false);
                      setClientName(client);
                      setClientNameError('');
                    }}>
                    Cancel
                  </Button>
                </Space>
              </div>
            </div>
          }>
          {status !== 'APPROVED' && prevState !== 'APPROVED' && (
            <EditIcon className={`edit-icon ${clientVisible ? 'proposal-item-name-active' : ''}`} />
          )}
        </Popover>
      </Col>

        <Col {...proposalItem.watched} className="value value-watched">
          <Popover
            overlayClassName="proposal-item-view-info-popover"
            content={'The total amount of minutes the client watched your proposal'}>
            <span
              onClick={(e) => {
                e.stopPropagation();
                onClickItem({ actionType: 'open_logs', proposalId });
              }}>
              {watched} {isMobile && opened ? `(Opened ${opened})`: ''}
            </span>
          </Popover>
          {watched ? (
            <EyeIcon
              className="open-logs-icon"
              onClick={(e) => {
                e.stopPropagation();
                onClickItem({ actionType: 'open_logs', proposalId });
              }}
            />
          ) : null}
        </Col>

      {!isMobile && (
        <Col {...proposalItem.opened} className="value value-opened">
          <Popover
            overlayClassName="proposal-item-view-info-popover"
            content={
              <span>
                The total number of times your client opened your proposal
                {lastSeen && <br></br>}
                {lastSeen ? ` Last opened: ${moment(lastSeen).format('MMM D, YYYY HH:mm')} ` : ''}
                {clientIP && <br></br>}
                {clientIP ? `Signature IP:  ${clientIP}` : ''}
              </span>
            }>
            <span
              onClick={(e) => {
                e.stopPropagation();
                onClickItem({ actionType: 'open_logs', proposalId });
              }}>
              {opened}
            </span>
          </Popover>
          {opened || emailLogs?.length ? (
            <EyeIcon
              className="open-logs-icon"
              onClick={(e) => {
                e.stopPropagation();
                onClickItem({ actionType: 'open_logs', proposalId });
              }}
            />
          ) : null}
        </Col>
      )}

      <Col {...proposalItem.quote} className="value value-quote">
        <Popover
          overlayClassName="proposal-item-view-info-popover"
          content={
            <span>
              {quote}
              <hr />
              The price range the client can choose from. The price can change based on Value cost
              option or Table cost option with checkboxes.
            </span>
          }>
          {quote.substring(0, 20)}
        </Popover>
      </Col>

      {isMobile && (
        <Col xs={24} className="divider-col">
          <Divider />
        </Col>
      )}

      <Col {...proposalItem.status} className="value value-status">
        {!isHighlighted &&
          status &&
          renderStatusTooltip(
            <StatusLabel status={status} className="status">
              {status.toLowerCase()}
            </StatusLabel>
          )}
        {isHighlighted &&
          status &&
          renderStatusTooltip(
            <StatusLabel
              status={status}
              onClick={(e) => {
                e.stopPropagation();
                handleSignedSeen(proposalId);
              }}
              className="status highlighted">
              <DismissBadge
                status={status}
                className="status dismiss"
                loading={loadingMap[proposalId]}>
                {!loadingMap[proposalId] && <CheckIcon />}
              </DismissBadge>
              <span>{status.toLowerCase()}</span>
            </StatusLabel>
          )}
      </Col>

      <Col {...proposalItem.action} className="value action-column">
        <Dropdown
          className="proposal-item-menu"
          overlay={trash ? (isDeletable ? trashMenu : null) : menu}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          trigger="click">
          <a
            ref={tourRef}
            className="ant-dropdown-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            <MenuIcon />
          </a>
        </Dropdown>
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <Tour
            open={openTour}
            onClose={closeTour}
            steps={tourSteps}
            indicatorsRender={false}
            visibleChange={handleVisibleChange}
            icon={<MenuIcon />}
            onSubmit={() => closeTour(true)}
          />
        </div>
      </Col>

      {isMobile && (
        <Col xs={24} className="value tags-column">
          {tagsDom}
        </Col>
      )}
    </Item>
  );
};

/**
 * Proposal item default props
 */
ProposalItem.defaultProps = {
  lastSeen: '',
  clientIP: '',
  clientNames: [],
  opened: null,
  teamInfo: null,
  isLocked: false,
  loadProposalClients: () => {},
  tags: [],
  tagsData: [],
  lastStatusUpdateTime: '',
  lockLoadingMap: {},
};

/**
 * Proposal item prop types
 */
ProposalItem.propTypes = {
  pnum: PropTypes.string,
  proposalId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  client: PropTypes.string.isRequired,
  watched: PropTypes.string.isRequired,
  quote: PropTypes.string.isRequired,
  opened: PropTypes.number,
  onClickItem: PropTypes.func.isRequired,
  loadProposalClients: PropTypes.func.isRequired,
  clientNames: PropTypes.instanceOf(Array),
  status: PropTypes.oneOf([
    'PUBLISHED',
    'DOWNLOADED',
    'APPROVED',
    'DRAFT',
    'OPENED',
    'APPROVED',
    'DECLINED',
    'DELETED',
  ]).isRequired,
  lastSeen: PropTypes.string,
  clientIP: PropTypes.string,
  teamInfo: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.instanceOf(null)]),
  isLocked: PropTypes.bool,
  tags: PropTypes.array,
  tagsData: PropTypes.array,
  isHighlighted: PropTypes.bool,
  handleSignedSeen: PropTypes.func,
  lastStatusUpdateTime: PropTypes.string,
};

export default ProposalItem;
